import { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Pencil, Trash2, X, FlaskConical } from 'lucide-react';
import toast from '../Toast';
import {
  adminErrorRuleAPI,
  type UserFacingErrorRule,
  type UserFacingErrorRuleInput,
  type UserFacingErrorRuleCategory,
} from '../../services/api';
import { formatBeijingDateTime } from '../../utils/time';

type RuleFormState = UserFacingErrorRuleInput;

const EMPTY_FORM: RuleFormState = {
  mediaType: 'all',
  matchType: 'contains',
  pattern: '',
  userMessage: '',
  priority: 100,
  enabled: true,
  stopChain: false,
  category: 'unknown',
  note: '',
};

const CATEGORY_LABELS: Record<UserFacingErrorRuleCategory, string> = {
  moderation: '内容审核',
  infrastructure: '基础设施',
  generation: '生成异常',
  param: '参数错误',
  config: '配置错误',
  unknown: '未分类',
};

const CATEGORY_STYLES: Record<UserFacingErrorRuleCategory, string> = {
  moderation: 'bg-rose-50 text-rose-700',
  infrastructure: 'bg-orange-50 text-orange-700',
  generation: 'bg-blue-50 text-blue-700',
  param: 'bg-amber-50 text-amber-700',
  config: 'bg-purple-50 text-purple-700',
  unknown: 'bg-slate-100 text-slate-500',
};

export default function ErrorRulesTab() {
  const [rules, setRules] = useState<UserFacingErrorRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<UserFacingErrorRule | null>(null);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [testMediaType, setTestMediaType] = useState<'image' | 'video'>('video');
  const [testErrorMessage, setTestErrorMessage] = useState('');
  const [testResult, setTestResult] = useState<{
    matchedRule: UserFacingErrorRule | null;
    analysisText: string;
    finalUserMessage: string | null;
  } | null>(null);

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt)),
    [rules]
  );

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await adminErrorRuleAPI.list();
      setRules(res.data);
    } catch (error) {
      console.error('加载错误映射规则失败:', error);
      toast.error(error instanceof Error ? error.message : '加载错误映射规则失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
  }, []);

  const openCreateModal = () => {
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (rule: UserFacingErrorRule) => {
    setEditingRule(rule);
    setForm({
      mediaType: rule.mediaType,
      matchType: 'contains',
      pattern: rule.pattern,
      userMessage: rule.userMessage,
      priority: rule.priority,
      enabled: rule.enabled,
      stopChain: rule.stopChain,
      category: rule.category,
      note: rule.note || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleSave = async () => {
    if (!form.pattern.trim()) {
      toast.error('匹配内容不能为空');
      return;
    }
    if (!form.userMessage.trim()) {
      toast.error('用户文案不能为空');
      return;
    }

    setSaving(true);
    try {
      const payload: UserFacingErrorRuleInput = {
        ...form,
        pattern: form.pattern.trim(),
        userMessage: form.userMessage.trim(),
        note: form.note?.trim() || '',
      };
      if (editingRule) {
        await adminErrorRuleAPI.update(editingRule.id, payload);
        toast.success('规则已更新');
      } else {
        await adminErrorRuleAPI.create(payload);
        toast.success('规则已创建');
      }
      setShowModal(false);
      await loadRules();
    } catch (error) {
      console.error('保存错误映射规则失败:', error);
      toast.error(error instanceof Error ? error.message : '保存错误映射规则失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rule: UserFacingErrorRule) => {
    if (!window.confirm(`确认删除规则「${rule.pattern}」吗？`)) return;
    try {
      await adminErrorRuleAPI.remove(rule.id);
      toast.success('规则已删除');
      await loadRules();
    } catch (error) {
      console.error('删除错误映射规则失败:', error);
      toast.error(error instanceof Error ? error.message : '删除错误映射规则失败');
    }
  };

  const handleTest = async () => {
    if (!testErrorMessage.trim()) {
      toast.error('请先输入一段原始报错');
      return;
    }
    setTesting(true);
    try {
      const res = await adminErrorRuleAPI.test({
        mediaType: testMediaType,
        errorMessage: testErrorMessage,
      });
      setTestResult(res.data);
    } catch (error) {
      console.error('测试错误映射规则失败:', error);
      toast.error(error instanceof Error ? error.message : '测试错误映射规则失败');
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-light-border bg-white p-4 shadow-sm">
            <p className="text-sm text-light-muted">规则总数</p>
            <p className="mt-2 text-2xl font-bold text-light-text">{rules.length}</p>
          </div>
          <div className="rounded-2xl border border-light-border bg-white p-4 shadow-sm">
            <p className="text-sm text-light-muted">启用规则</p>
            <p className="mt-2 text-2xl font-bold text-light-text">{rules.filter((item) => item.enabled).length}</p>
          </div>
          <div className="rounded-2xl border border-light-border bg-white p-4 shadow-sm">
            <p className="text-sm text-light-muted">适用范围</p>
            <p className="mt-2 text-sm font-medium text-light-text">图片与视频共用，不区分 provider</p>
          </div>
        </div>

        <div className="rounded-2xl border border-light-border bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-light-border p-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-light-text">规则管理</h3>
              <p className="mt-1 text-sm text-light-muted">按优先级匹配原始报错，输出用户侧安全文案</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={loadRules}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-light-hover px-4 py-2 text-sm text-light-text transition-colors hover:bg-light-border"
              >
                <RefreshCw className="h-4 w-4" />
                刷新
              </button>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-light-text px-4 py-2 text-sm text-white transition-opacity hover:opacity-90"
              >
                <Plus className="h-4 w-4" />
                新建规则
              </button>
            </div>
          </div>

          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full">
              <thead>
                <tr className="bg-light-hover text-left text-sm text-light-muted">
                  <th className="px-5 py-3 font-medium">优先级</th>
                  <th className="px-5 py-3 font-medium">分类</th>
                  <th className="px-5 py-3 font-medium">适用媒体</th>
                  <th className="px-5 py-3 font-medium">匹配内容</th>
                  <th className="px-5 py-3 font-medium">用户文案</th>
                  <th className="px-5 py-3 font-medium">状态</th>
                  <th className="px-5 py-3 font-medium">更新时间</th>
                  <th className="px-5 py-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-light-border">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-10 text-center text-light-muted">加载中...</td>
                  </tr>
                ) : sortedRules.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-10 text-center text-light-muted">暂无规则</td>
                  </tr>
                ) : (
                  sortedRules.map((rule) => (
                    <tr key={rule.id} className="align-top text-sm text-light-text">
                      <td className="px-5 py-4 font-medium">{rule.priority}</td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${CATEGORY_STYLES[rule.category] ?? CATEGORY_STYLES.unknown}`}
                        >
                          {CATEGORY_LABELS[rule.category] ?? CATEGORY_LABELS.unknown}
                        </span>
                      </td>
                      <td className="px-5 py-4">{rule.mediaType === 'all' ? '全部' : rule.mediaType}</td>
                      <td className="px-5 py-4">
                        <div className="max-w-xs whitespace-pre-wrap break-all font-mono text-xs text-light-secondary">
                          {rule.pattern}
                        </div>
                        {rule.note && <div className="mt-2 text-xs text-light-muted">{rule.note}</div>}
                      </td>
                      <td className="px-5 py-4">
                        <div className="max-w-sm whitespace-pre-wrap break-words">{rule.userMessage}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-1.5">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                              rule.enabled
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {rule.enabled ? '启用' : '停用'}
                          </span>
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                              rule.stopChain
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {rule.stopChain ? '停止链路' : '继续链路'}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-xs text-light-muted">
                        {formatBeijingDateTime(rule.updatedAt)}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(rule)}
                            className="inline-flex items-center gap-1 rounded-lg bg-light-hover px-3 py-1.5 text-xs text-light-text transition-colors hover:bg-light-border"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            编辑
                          </button>
                          <button
                            onClick={() => handleDelete(rule)}
                            className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-3 py-1.5 text-xs text-rose-600 transition-colors hover:bg-rose-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 p-4 lg:hidden">
            {loading ? (
              <div className="rounded-xl border border-light-border px-4 py-8 text-center text-light-muted">加载中...</div>
            ) : sortedRules.length === 0 ? (
              <div className="rounded-xl border border-light-border px-4 py-8 text-center text-light-muted">暂无规则</div>
            ) : (
              sortedRules.map((rule) => (
                <div key={rule.id} className="rounded-2xl border border-light-border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-light-text">优先级 {rule.priority}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-light-muted">适用媒体：{rule.mediaType === 'all' ? '全部' : rule.mediaType}</span>
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${CATEGORY_STYLES[rule.category] ?? CATEGORY_STYLES.unknown}`}>
                          {CATEGORY_LABELS[rule.category] ?? CATEGORY_LABELS.unknown}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <span className={`rounded-full px-2 py-1 text-xs ${rule.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {rule.enabled ? '启用' : '停用'}
                      </span>
                      <span className={`rounded-full px-2 py-1 text-xs ${rule.stopChain ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                        {rule.stopChain ? '停止链路' : '继续链路'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 whitespace-pre-wrap break-all rounded-xl bg-light-hover p-3 font-mono text-xs text-light-secondary">
                    {rule.pattern}
                  </div>
                  <div className="mt-3 whitespace-pre-wrap break-words text-sm text-light-text">{rule.userMessage}</div>
                  {rule.note && <div className="mt-2 text-xs text-light-muted">{rule.note}</div>}
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => openEditModal(rule)} className="rounded-lg bg-light-hover px-3 py-1.5 text-xs text-light-text">编辑</button>
                    <button onClick={() => handleDelete(rule)} className="rounded-lg bg-rose-50 px-3 py-1.5 text-xs text-rose-600">删除</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-light-border bg-white shadow-sm">
          <div className="border-b border-light-border p-4">
            <h3 className="text-lg font-semibold text-light-text">测试面板</h3>
            <p className="mt-1 text-sm text-light-muted">粘贴一段原始错误，预览会命中哪条规则</p>
          </div>
          <div className="grid gap-4 p-4 lg:grid-cols-[220px,1fr]">
            <div className="space-y-2">
              <label className="text-sm font-medium text-light-text">媒体类型</label>
              <select
                value={testMediaType}
                onChange={(e) => setTestMediaType(e.target.value as 'image' | 'video')}
                className="w-full rounded-xl border border-light-border px-3 py-2 text-sm focus:border-accent-blue focus:outline-none"
              >
                <option value="video">视频</option>
                <option value="image">图片</option>
              </select>
              <button
                onClick={handleTest}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-light-text px-4 py-2 text-sm text-white transition-opacity hover:opacity-90"
              >
                <FlaskConical className="h-4 w-4" />
                {testing ? '测试中...' : '开始测试'}
              </button>
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-light-text">
                原始报错
                <textarea
                  value={testErrorMessage}
                  onChange={(e) => setTestErrorMessage(e.target.value)}
                  rows={7}
                  className="mt-2 w-full rounded-2xl border border-light-border px-4 py-3 text-sm text-light-text outline-none focus:border-accent-blue"
                  placeholder="例如：Please try a new call. Your current call may be flagged as containing prohibited words or images. [task_failed]"
                />
              </label>
              <div className="rounded-2xl border border-dashed border-light-border bg-light-hover/40 p-4">
                <div className="text-sm font-medium text-light-text">测试结果</div>
                {!testResult ? (
                  <div className="mt-2 text-sm text-light-muted">尚未测试</div>
                ) : (
                  <div className="mt-3 space-y-3 text-sm">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-light-muted">最终用户文案</div>
                      <div className="mt-1 rounded-xl bg-white px-3 py-2 text-light-text">
                        {testResult.finalUserMessage || '未命中规则'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-light-muted">命中规则</div>
                      <div className="mt-1 rounded-xl bg-white px-3 py-2 text-light-text">
                        {testResult.matchedRule ? `#${testResult.matchedRule.priority} - ${testResult.matchedRule.pattern}` : '未命中'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-light-muted">归一化分析文本</div>
                      <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded-xl bg-white px-3 py-2 text-xs text-light-secondary">
                        {testResult.analysisText || '-'}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-light-border px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-light-text">{editingRule ? '编辑规则' : '新建规则'}</h3>
                <p className="mt-1 text-sm text-light-muted">当前统一使用包含匹配，不区分 provider</p>
              </div>
              <button onClick={closeModal} className="rounded-lg p-2 transition-colors hover:bg-light-hover">
                <X className="h-5 w-5 text-light-muted" />
              </button>
            </div>
            <div className="space-y-4 overflow-y-auto p-6">
              <div className="grid gap-4 md:grid-cols-4">
                <label className="space-y-2 text-sm font-medium text-light-text">
                  适用媒体
                  <select
                    value={form.mediaType}
                    onChange={(e) => setForm((prev) => ({ ...prev, mediaType: e.target.value as RuleFormState['mediaType'] }))}
                    className="w-full rounded-xl border border-light-border px-3 py-2 text-sm focus:border-accent-blue focus:outline-none"
                  >
                    <option value="all">全部</option>
                    <option value="image">图片</option>
                    <option value="video">视频</option>
                  </select>
                </label>
                <label className="space-y-2 text-sm font-medium text-light-text">
                  匹配方式
                  <input
                    value="包含匹配"
                    readOnly
                    className="w-full rounded-xl border border-light-border bg-light-hover px-3 py-2 text-sm text-light-muted outline-none"
                  />
                </label>
                <label className="space-y-2 text-sm font-medium text-light-text">
                  优先级
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm((prev) => ({ ...prev, priority: Number(e.target.value) || 0 }))}
                    className="w-full rounded-xl border border-light-border px-3 py-2 text-sm focus:border-accent-blue focus:outline-none"
                  />
                </label>
                <label className="space-y-2 text-sm font-medium text-light-text">
                  分类
                  <select
                    value={form.category}
                    onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value as UserFacingErrorRuleCategory }))}
                    className="w-full rounded-xl border border-light-border px-3 py-2 text-sm focus:border-accent-blue focus:outline-none"
                  >
                    <option value="moderation">内容审核</option>
                    <option value="infrastructure">基础设施</option>
                    <option value="generation">生成异常</option>
                    <option value="param">参数错误</option>
                    <option value="config">配置错误</option>
                    <option value="unknown">未分类</option>
                  </select>
                </label>
              </div>

              <label className="block space-y-2 text-sm font-medium text-light-text">
                匹配内容
                <textarea
                  value={form.pattern}
                  onChange={(e) => setForm((prev) => ({ ...prev, pattern: e.target.value }))}
                  rows={4}
                  className="w-full rounded-2xl border border-light-border px-4 py-3 font-mono text-sm outline-none focus:border-accent-blue"
                  placeholder="例如 prohibited words or images"
                />
              </label>

              <label className="block space-y-2 text-sm font-medium text-light-text">
                用户文案
                <textarea
                  value={form.userMessage}
                  onChange={(e) => setForm((prev) => ({ ...prev, userMessage: e.target.value }))}
                  rows={3}
                  className="w-full rounded-2xl border border-light-border px-4 py-3 text-sm outline-none focus:border-accent-blue"
                  placeholder="例如 提示词或参考素材可能涉及敏感内容，请调整后重试"
                />
              </label>

              <label className="block space-y-2 text-sm font-medium text-light-text">
                备注
                <textarea
                  value={form.note || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                  rows={2}
                  className="w-full rounded-2xl border border-light-border px-4 py-3 text-sm outline-none focus:border-accent-blue"
                  placeholder="可选，用于记录这条规则的使用场景"
                />
              </label>

              <div className="space-y-3 rounded-2xl border border-light-border bg-light-hover/40 p-4">
                <label className="inline-flex items-center gap-3 text-sm text-light-text">
                  <input
                    type="checkbox"
                    checked={form.stopChain}
                    onChange={(e) => setForm((prev) => ({ ...prev, stopChain: e.target.checked }))}
                    className="h-4 w-4 rounded border-light-border"
                  />
                  命中后停止后续链路
                </label>
                <p className="text-xs leading-5 text-light-muted">
                  仅对确定性错误启用，例如明确的内容违规或无效请求；网络波动、限流等可恢复错误应继续链路。
                </p>
              </div>

              <label className="inline-flex items-center gap-3 text-sm text-light-text">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
                  className="h-4 w-4 rounded border-light-border"
                />
                启用这条规则
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-light-border px-6 py-4">
              <button onClick={closeModal} className="rounded-xl border border-light-border px-4 py-2 text-sm text-light-text">
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="rounded-xl bg-light-text px-4 py-2 text-sm text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? '保存中...' : '保存规则'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
